import { accentuateApiRef } from '@internal/backstage-plugin-accentuate';
import { useState } from 'react';

import { alertApiRef, useApi } from '@backstage/core-plugin-api';

import { OwnershipEntity, OwnershipHandlerResult } from '../types';

/**
 * Extended handler result that includes markAsUnknown.
 */
export interface GenericOwnershipHandlerResult extends OwnershipHandlerResult {
  markAsUnknown: (entities: OwnershipEntity[]) => Promise<boolean>;
}

/**
 * Hook for handling generic entity ownership via accentuate API.
 * Works for any entity kind that doesn't have a specific handler.
 */
export function useGenericOwnershipHandler(): GenericOwnershipHandlerResult {
  const accentuateApi = useApi(accentuateApiRef);
  const alertApi = useApi(alertApiRef);
  const [loading, setLoading] = useState(false);

  const submit = async (
    entities: OwnershipEntity[],
    ownerRef: string,
  ): Promise<boolean> => {
    setLoading(true);
    try {
      // Normalize owner ref
      const normalizedOwnerRef = ownerRef.includes(':')
        ? ownerRef
        : `group:default/${ownerRef}`;

      // Update each entity
      for (const entity of entities) {
        await accentuateApi.update(entity.entityRef, {
          spec: { owner: normalizedOwnerRef },
          metadata: {
            annotations: {
              'tomtom.com/ownership-status': 'confirmed',
            },
          },
        });

        // Refresh the entity
        await accentuateApi.refresh(entity.entityRef);
      }

      const entityLabel = entities.length === 1 ? 'entity' : 'entities';
      alertApi.post({
        message: `Successfully updated ownership for ${entities.length} ${entityLabel}. Please refresh the page to see the changes.`,
        severity: 'success',
        display: 'transient',
      });

      setLoading(false);
      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      alertApi.post({
        message: `Failed to update ownership: ${errorMessage}`,
        severity: 'error',
      });
      setLoading(false);
      return false;
    }
  };

  const markAsUnknown = async (
    entities: OwnershipEntity[],
  ): Promise<boolean> => {
    setLoading(true);
    try {
      for (const entity of entities) {
        await accentuateApi.update(entity.entityRef, {
          spec: { owner: 'unknown' },
          metadata: {
            annotations: {
              'tomtom.com/ownership-status': 'proposed',
            },
          },
        });

        await accentuateApi.refresh(entity.entityRef);
      }

      const entityLabel = entities.length === 1 ? 'entity' : 'entities';
      alertApi.post({
        message: `Marked ${entities.length} ${entityLabel} as having unknown ownership. Please refresh the page to see the changes.`,
        severity: 'info',
        display: 'transient',
      });

      setLoading(false);
      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      alertApi.post({
        message: `Failed to mark as unknown: ${errorMessage}`,
        severity: 'error',
      });
      setLoading(false);
      return false;
    }
  };

  return { submit, loading, markAsUnknown };
}
